import { pendingPayoutsByPayoutCurrency } from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  Company,
  InvoiceItem,
  MonthSplit,
  PendingPayout,
  PendingPayoutByCompanyId,
} from "@/types";
import { computed, type MaybeRefOrGetter, ref, toValue } from "vue";

type PendingInvoiceItemsResponse = {
  invoice_items: InvoiceItem[];
  default_invoice_number: string;
  month_split: MonthSplit | null;
  pending_payout: PendingPayout | null;
};

/**
 * Holds a company's invoice items still open for settlement, i.e. what Soldera owes the company
 * net of fees, as served by `GET /api/company/{id}/pending-invoice-items`, with the pending payout
 * the backend computes from them, and decides from those, in the company's payout currency, whether
 * money is pending.
 */
export const usePendingInvoiceItems = (company: MaybeRefOrGetter<Company>) => {
  const pendingInvoiceItems = ref<InvoiceItem[]>([]);
  const defaultInvoiceNumber = ref("");
  const monthSplit = ref<MonthSplit | null>(null);
  const pendingPayoutByCompanyId = ref<PendingPayoutByCompanyId>({});

  const pendingPayouts = computed(() =>
    pendingPayoutsByPayoutCurrency(
      pendingInvoiceItems.value,
      [toValue(company)],
      pendingPayoutByCompanyId.value
    )
  );
  // A fee-only or net-negative balance is settled against sales, not invoiced
  const canSubmitInvoice = computed(() => pendingPayouts.value.length > 0);

  const clearPendingInvoiceItems = () => {
    pendingInvoiceItems.value = [];
    monthSplit.value = null;
    pendingPayoutByCompanyId.value = {};
  };

  /**
   * Loads the company's pending items and default invoice number. The previous company's items
   * are dropped first, so a failed fetch never leaves them on screen under another company.
   *
   * @throws The API error, for the caller to hand to the global error handler
   */
  const fetchPendingInvoiceItems = async (companyId: number) => {
    clearPendingInvoiceItems();
    defaultInvoiceNumber.value = "";
    const response = await goSolidApi.get<PendingInvoiceItemsResponse>(
      `/api/company/${companyId}/pending-invoice-items`
    );
    pendingInvoiceItems.value = response.data.invoice_items;
    defaultInvoiceNumber.value = response.data.default_invoice_number;
    monthSplit.value = response.data.month_split;
    pendingPayoutByCompanyId.value = {
      [companyId]: response.data.pending_payout,
    };
  };

  return {
    pendingInvoiceItems,
    defaultInvoiceNumber,
    monthSplit,
    pendingPayoutByCompanyId,
    pendingPayouts,
    canSubmitInvoice,
    fetchPendingInvoiceItems,
    clearPendingInvoiceItems,
  };
};
